import type { 
  ConsentRecord, 
  ConsentType, 
  CookieCategory, 
  PrivacySettings, 
  DataExportRequest, 
  DataDeletionRequest,
  PrivacyAuditLog 
} from '@/types/gdpr';

class GDPRService {
  private readonly STORAGE_KEYS = {
    CONSENT: 'taskbridge_consent',
    PRIVACY_SETTINGS: 'taskbridge_privacy_settings',
    AUDIT_LOG: 'taskbridge_audit_log'
  };

  // Cookie Categories Configuration
  getCookieCategories(): CookieCategory[] {
    return [
      {
        id: 'essential',
        name: 'Essential Cookies',
        description: 'Required for basic website functionality and security',
        essential: true,
        cookies: [
          { name: 'auth_token', purpose: 'User authentication', duration: '30 days', provider: 'TaskBridge' },
          { name: 'session_id', purpose: 'Session management', duration: 'Session', provider: 'TaskBridge' }
        ]
      },
      {
        id: 'analytics',
        name: 'Analytics Cookies',
        description: 'Help us understand how you use our website to improve performance',
        essential: false,
        cookies: [
          { name: '_ga', purpose: 'Google Analytics tracking', duration: '2 years', provider: 'Google' },
          { name: 'usage_stats', purpose: 'Internal usage analytics', duration: '1 year', provider: 'TaskBridge' }
        ]
      },
      {
        id: 'marketing',
        name: 'Marketing Cookies',
        description: 'Used to show you relevant advertisements and measure campaign effectiveness',
        essential: false,
        cookies: [
          { name: 'ad_preferences', purpose: 'Personalized advertising', duration: '1 year', provider: 'TaskBridge' },
          { name: '_fbp', purpose: 'Facebook advertising', duration: '90 days', provider: 'Facebook' }
        ]
      },
      {
        id: 'personalization',
        name: 'Personalization',
        description: 'Remember your preferences and customize your experience',
        essential: false,
        cookies: [
          { name: 'user_preferences', purpose: 'UI preferences', duration: '1 year', provider: 'TaskBridge' },
          { name: 'location_cache', purpose: 'Remember location settings', duration: '6 months', provider: 'TaskBridge' }
        ]
      }
    ];
  }

  // Consent Management
  getConsent(userId: string): Record<ConsentType, boolean> {
    const stored = localStorage.getItem(`${this.STORAGE_KEYS.CONSENT}_${userId}`);
    if (stored) {
      return JSON.parse(stored);
    }
    
    // Default consent (only essential)
    return {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false,
      third_party: false,
      location: false,
      communications: false
    };
  }

  updateConsent(userId: string, consentType: ConsentType, granted: boolean): void {
    const currentConsent = this.getConsent(userId);
    currentConsent[consentType] = granted;
    
    localStorage.setItem(`${this.STORAGE_KEYS.CONSENT}_${userId}`, JSON.stringify(currentConsent));
    
    // Record consent change
    this.recordConsentChange(userId, consentType, granted);
    this.logAuditEvent(userId, 'consent_change', `${consentType}: ${granted}`);
  }

  updateAllConsent(userId: string, consent: Record<ConsentType, boolean>): void {
    // Essential cookies cannot be disabled
    consent.essential = true;
    
    localStorage.setItem(`${this.STORAGE_KEYS.CONSENT}_${userId}`, JSON.stringify(consent));
    
    // Record all consent changes
    Object.entries(consent).forEach(([type, granted]) => {
      this.recordConsentChange(userId, type as ConsentType, granted);
    });
    
    this.logAuditEvent(userId, 'consent_change', 'Bulk consent update');
  }

  private recordConsentChange(userId: string, consentType: ConsentType, granted: boolean): void {
    const record: ConsentRecord = {
      id: `consent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId,
      consentType,
      granted,
      timestamp: new Date(),
      version: '1.0' // Privacy policy version
    };

    const records = this.getConsentHistory(userId);
    records.push(record);
    localStorage.setItem(`consent_history_${userId}`, JSON.stringify(records));
  }

  getConsentHistory(userId: string): ConsentRecord[] {
    const stored = localStorage.getItem(`consent_history_${userId}`);
    return stored ? JSON.parse(stored) : [];
  }

  // Privacy Settings Management
  getPrivacySettings(userId: string): PrivacySettings {
    const stored = localStorage.getItem(`${this.STORAGE_KEYS.PRIVACY_SETTINGS}_${userId}`);
    if (stored) {
      const settings = JSON.parse(stored);
      return { ...settings, updatedAt: new Date(settings.updatedAt) };
    }

    // Default privacy settings
    return {
      userId,
      profileVisibility: 'limited',
      showLocation: true,
      allowDirectContact: true,
      marketingEmails: false,
      analyticsTracking: false,
      dataRetention: 'standard',
      anonymousReviews: false,
      updatedAt: new Date()
    };
  }

  updatePrivacySettings(userId: string, settings: Partial<PrivacySettings>): void {
    const current = this.getPrivacySettings(userId);
    const updated = { ...current, ...settings, updatedAt: new Date() };
    
    localStorage.setItem(`${this.STORAGE_KEYS.PRIVACY_SETTINGS}_${userId}`, JSON.stringify(updated));
    this.logAuditEvent(userId, 'settings_update', 'Privacy settings updated');
  }

  // Data Export
  requestDataExport(userId: string, format: 'json' | 'csv' | 'pdf' = 'json'): DataExportRequest {
    const request: DataExportRequest = {
      id: `export_${Date.now()}`,
      userId,
      requestDate: new Date(),
      status: 'pending',
      format
    };

    // Simulate processing
    setTimeout(() => {
      this.processDataExport(request);
    }, 2000);

    this.logAuditEvent(userId, 'data_export', `Export requested in ${format} format`);
    return request;
  }

  private processDataExport(request: DataExportRequest): void {
    // Simulate data compilation
    const userData = this.compileUserData(request.userId);
    const blob = new Blob([JSON.stringify(userData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    request.status = 'completed';
    request.downloadUrl = url;
    request.expiryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days
  }

  private compileUserData(userId: string): any {
    return {
      exportDate: new Date().toISOString(),
      userId,
      personalData: {
        profile: "User profile data would be here",
        preferences: this.getPrivacySettings(userId),
        consent: this.getConsent(userId)
      },
      activityData: {
        jobs: "Job history data",
        messages: "Message history data",
        reviews: "Review data"
      },
      consentHistory: this.getConsentHistory(userId),
      auditLog: this.getAuditLog(userId)
    };
  }

  // Data Deletion
  requestDataDeletion(userId: string, reason?: string): DataDeletionRequest {
    const request: DataDeletionRequest = {
      id: `deletion_${Date.now()}`,
      userId,
      requestDate: new Date(),
      scheduledDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
      status: 'scheduled',
      reason,
      retainedData: ['financial_records', 'legal_compliance'] // What we must keep
    };

    this.logAuditEvent(userId, 'data_deletion', `Deletion scheduled for ${request.scheduledDate}`);
    return request;
  }

  // Audit Logging
  logAuditEvent(userId: string, action: PrivacyAuditLog['action'], details: string): void {
    const log: PrivacyAuditLog = {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId,
      action,
      details,
      timestamp: new Date()
    };

    const logs = this.getAuditLog(userId);
    logs.push(log);
    
    // Keep only last 100 entries
    if (logs.length > 100) {
      logs.splice(0, logs.length - 100);
    }
    
    localStorage.setItem(`${this.STORAGE_KEYS.AUDIT_LOG}_${userId}`, JSON.stringify(logs));
  }

  getAuditLog(userId: string): PrivacyAuditLog[] {
    const stored = localStorage.getItem(`${this.STORAGE_KEYS.AUDIT_LOG}_${userId}`);
    if (stored) {
      const logs = JSON.parse(stored);
      return logs.map((log: any) => ({ ...log, timestamp: new Date(log.timestamp) }));
    }
    return [];
  }

  // Cookie Management
  hasConsent(userId: string, type: ConsentType): boolean {
    const consent = this.getConsent(userId);
    return consent[type] || false;
  }

  // Check if user has made any consent decisions
  hasConsentDecision(userId: string): boolean {
    const stored = localStorage.getItem(`${this.STORAGE_KEYS.CONSENT}_${userId}`);
    return !!stored;
  }
}

export const gdprService = new GDPRService();